import { Rune, Unit, AccountBonus } from '../Page/runeEngine';

/**
 * **읽어 둔 계정을 이 브라우저에 담아 둔다** — 룬최적화 탭을 다시 열 때 파일을 또 고르지 않게.
 *
 * 서버로 보내지 않는다(화면에도 그렇게 적어 두었다). 담는 것은 **쓰는 것만** — 룬·몹·건물 보너스와
 * 이름표뿐이고, 계정 JSON 의 나머지(우편·아이템·친구 목록…)는 버린다. 7MB 원본 대신 1MB 남짓이다.
 *
 * localStorage 는 5MB 언저리라 모자라서 IndexedDB 를 쓴다. 라이브러리 없이 스무 줄이면 된다.
 * 사생활 보호 모드나 저장이 막힌 브라우저에서는 조용히 실패하고(담기지 않을 뿐) 화면은 그대로 돈다.
 */

const DB_NAME = 'swpvpdb-rune';
const STORE = 'account';
const KEY = 'last';

export interface SavedAccount {
    savedAt: number;
    wizardName: string | null;
    equipped: number;
    stored: number;
    bonus: AccountBonus;
    runes: Rune[];
    units: Unit[];
}

function open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T | null> {
    return open()
        .then((db) => new Promise<T | null>((resolve, reject) => {
            const tx = db.transaction(STORE, mode);
            const req = fn(tx.objectStore(STORE));
            req.onsuccess = () => resolve((req.result as T) ?? null);
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => db.close();
        }))
        .catch(() => null);   // 저장이 막힌 브라우저 — 없는 셈 친다
}

export const saveAccount = (a: SavedAccount) => run<void>('readwrite', (s) => s.put(a, KEY));
export const loadAccount = () => run<SavedAccount>('readonly', (s) => s.get(KEY));
export const clearAccount = () => run<void>('readwrite', (s) => s.delete(KEY));

/**
 * **공덱 유전 학습용 계정 원본** — swSim 이 룬을 게임 모양 그대로 받아 셈하므로, 위의 정리본과 따로 **룬·보유몹 원본만** 담는다
 * (사용자 결정 2026-10-16 "룬·보유몹만 서버로 보냄" — 학습할 때만 보내고 서버는 저장하지 않는다).
 */
export interface RawAccount {
    savedAt: number;
    wizardName: string | null;
    units: any[];
    runes: any[];
    /** 아티팩트 보관함 — [AI 룬작에 내 룬 맞추기](2026-10-27)부터 담는다. 그 전에 담은 계정에는 없다(몹이 낀 것만 쓴다) */
    artifacts?: any[];
    /** 유물 보관함 — 룬피팅·룬 고급 설정이 유물 주옵까지 넣어 맞춘다(2026-10-28). 그 전에 담은 계정에는 없다(몹이 새긴 것만 쓴다) */
    relics?: any[];
}
const RAW_KEY = 'gaRaw';
export const saveRawAccount = (a: RawAccount) => run<void>('readwrite', (s) => s.put(a, RAW_KEY));
export const loadRawAccount = () => run<RawAccount>('readonly', (s) => s.get(RAW_KEY));
export const clearRawAccount = () => run<void>('readwrite', (s) => s.delete(RAW_KEY));

/** SWEX 계정 JSON → 학습에 쓰는 것만 (보관소 + 일반 봉인 몹의 번호·레벨·성급·룬·아티팩트·유물, 창고 룬·아티팩트) */
export function rawAccountOf(json: any): RawAccount {
    if (!json || !Array.isArray(json.unit_list)) throw new Error('SWEX 계정 JSON 이 아닙니다 (unit_list 가 없습니다)');
    const arr = (v: any) => (Array.isArray(v) ? v : v && typeof v === 'object' ? Object.values(v) : []);
    const units = [...json.unit_list, ...(json.unit_storage_normal_list || [])].map((u: any) => ({
        unit_id: u.unit_id, unit_master_id: u.unit_master_id, unit_level: u.unit_level, class: u.class,
        runes: arr(u.runes), artifacts: arr(u.artifacts), relics: arr(u.relics)
    }));
    return { savedAt: Date.now(), wizardName: json.wizard_info?.wizard_name || null, units, runes: arr(json.runes), artifacts: arr(json.artifacts), relics: arr(json.relics) };
}
